import { InlineEditorCancelButton, InlineEditorLogic, InlineEditorLogic_EditMode, InlineEditorLogic_ReadonlyMode, InlineEditorSaveButton, isEditMode, isReadonlyMode, useInlineEditor } from '@tableau/vizportalclient-common-components';
import { ButtonWidget, CompactInlineAlertWidget, InlineAlertType, TextFieldWidget } from '@tableau/widgets';
import { useFormikContext } from 'formik';
import React from 'react';
import { classes } from 'typestyle';
import _ from 'underscore';

import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { wideField } from '~src/settings/general-settings/styles/site-name-and-id/SiteNameAndIdStyles';
import { INPUT_WIDE_WIDTH } from '~src/settings/styles/CommonStyles';
import { cssClass } from '~src/style';

import { EmailCustomizationFormFields, EmailCustomizationSettingsSectionProps } from './EmailCustomizationSettingsSection';
import { isValidDomain } from './helpers/domainVerification';
import { ISiteSMTPSettings } from './serverApi/requests/SetSiteSMTPCredentialTagRequest';
import { ExternalSMTPConfiguration } from './serverApi/SiteSMTPCredential';

export const SMTP_CREDENTIALS_SECTION = 'smtp-credentials-section';

export const READONLY_CREDENTIALS_SECTION = 'readonly-credentials-section';
export const READONLY_SENT_FROM_TEST_ID = 'readonly-sent-from';
export const READONLY_DOMAIN_TEST_ID = 'readonly-domain';
export const READONLY_SERVER_ADDRESS_TEST_ID = 'readonly-server-address';
export const READONLY_PORT_TEST_ID = 'readonly-port';

export const EDIT_SERVER_ADDRESS_FIELD_TEST_ID = 'email-customization-smtp-address';
export const EDIT_PORT_FIELD_TEST_ID = 'email-customization-port';
export const EDIT_USERNAME_FIELD_TEST_ID = 'email-customization-username';
export const EDIT_PASSWORD_FIELD_TEST_ID = 'email-customization-password';
export const EDIT_FRIENDLY_NAME_FIELD_TEST_ID = 'email-customization-friendly-name';
export const EDIT_DOMAIN_FIELD_TEST_ID = 'email-customization-domain';
export const EDIT_LOCALNAME_FIELD_TEST_ID = 'email-customization-localname';

export const CREDENTIALS_FORM_ERROR_TEST_ID = 'credentials-form-error';

export const MAX_GENERAL_INPUT_LENGTH = 100;
export const MAX_DOMAIN_INPUT_LENGTH = 253;
const cssPrefix = 'EmailCustomization';

export const MAX_PORT = 65535;

export const DEFAULT_SENDER_FRIENDLY_NAME = 'no-reply';
export const DEFAULT_SENDER_LOCAL_NAME = 'no-reply';


const emailCustomizationInputClass = cssClass(cssPrefix, 'credentials-form', {
  display: 'flex',
  flexDirection: 'column',
});

const credentialsFormButtonsClass = cssClass(cssPrefix, 'credentials-form-buttons', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
});

const readonlyCredentialsClass = cssClass(cssPrefix, 'readonly-field', {
  paddingTop: 4,
  paddingBottom: 4,
  $nest: {
    '& span': {
      paddingRight: 4,
    }
  }
});

export interface CredentialsEditorState {
  host: string;
  port: string;
  username: string;
  friendlyName: string;
  password: string;
  domain: string;
  localName: string;
}

export interface EmailCustomizationOptionsProps extends EmailCustomizationSettingsSectionProps {
  onSave: (values: EmailCustomizationFormFields) => void;
}

export function EmailCustomizationOptions(props: EmailCustomizationOptionsProps) {
  return <SMTPCredentialsSection {...props} />;
}

export function SMTPCredentialsSection(props: EmailCustomizationOptionsProps) {
  const emailCustomizationFormik = useFormikContext<EmailCustomizationFormFields>();
  const useEmailCustomization = emailCustomizationFormik.values.isCustomizationEnabled;
  const [credentialsErrorMessage, setCredentialsErrorMessage] = React.useState<string>(undefined);

  const handleSave = async (editorState: CredentialsEditorState): Promise<void> => {
    const emailCustomizationSettings: ISiteSMTPSettings = {
      custom_domain: editorState.domain.trim(),
      sender_friendly_name: editorState.friendlyName?.trim(),
      server_password: editorState.password,
      server_port: parseInt(editorState.port),
      sender_local_name: editorState.localName?.trim(),
      server_username: editorState.username.trim(),
      server_url: editorState.host.trim(),
    };

    try {
      await ExternalSMTPConfiguration.setSiteSMTPCredential(emailCustomizationSettings, getSiteLuid());
      setCredentialsErrorMessage(undefined);
      saveFormHelper(editorState);

      ToasterService.getInstance().pop({
        type: ToastType.Success,
        text: Messages.emailServer_success(),
      });
    } catch (e) {
      setCredentialsErrorMessage(Messages.emailServer_errorSubmittingCredentials());
      throw e; // need to reject to stay in edit mode
    }
  };

  const saveFormHelper = (editorState: CredentialsEditorState) => {
    const values: EmailCustomizationFormFields = {
      isCustomizationEnabled: true,
      host: editorState.host,
      port: parseInt(editorState.port),
      username: editorState.username,
      friendlyName: editorState.friendlyName,
      domain: editorState.domain,
      password: editorState.password,
      localName: editorState.localName,
    };

    props.onSave(values);
    emailCustomizationFormik.resetForm({ values });
  };

  const canSave = (editorSavedState: CredentialsEditorState) => {
    const { host, port, username, friendlyName, password, domain, localName } = editorSavedState;

    const isValidHost = isHostValid(host);
    const isValidPort = isPortValid(port);
    const isValidFriendlyName = isFriendlyNameValid(friendlyName);
    const isValidPassword = isPasswordValid(password);
    const isValidDomain = isHostValid(domain);
    const isValidUsername = isUsernameValid(username);
    const isValidLocalName = isLocalNameValid(localName);

    const isFormDirty = host !== editorStateOnEdit.host ||
      port !== editorStateOnEdit.port ||
      username !== editorStateOnEdit.username ||
      friendlyName !== editorStateOnEdit.friendlyName ||
      domain !== editorStateOnEdit.domain ||
      localName !== editorStateOnEdit.localName ||
      password !== editorStateOnEdit.password;

    const isRequiredFieldEmpty = _.isEmpty(host) || _.isEmpty(port) || _.isEmpty(password) || _.isEmpty(domain) || _.isEmpty(username);

    return isValidHost && isValidPort && isValidUsername && isValidFriendlyName && isValidPassword && isValidDomain && isValidLocalName
      && isFormDirty && !isRequiredFieldEmpty;
  };

  const credentials = emailCustomizationFormik.values;
  const editorStateOnEdit: CredentialsEditorState = {
    host: credentials.host,
    port: credentials.port + '',
    username: '',
    localName: credentials.localName,
    friendlyName: credentials.friendlyName,
    domain: credentials.domain,
    password: '',
  };

  const handleCancel = () => {
    // if we don't already have credentials saved, canceling will go back to email customization off radio
    if (!credentials.host) {
      emailCustomizationFormik.setFieldValue('isCustomizationEnabled', false);
    }
  };

  const logic: InlineEditorLogic<CredentialsEditorState> = useInlineEditor<CredentialsEditorState>({
    editorStateWhenEnteringEditMode: editorStateOnEdit,
    canEdit: useEmailCustomization,
    onSave: handleSave,
    onCancel: handleCancel,
    canSaveFn: canSave,
  });

  React.useEffect(() => {
    if (!credentials.host && isReadonlyMode(logic) && useEmailCustomization) {
      logic.enterEditMode();
    }
  }, [credentials, logic, useEmailCustomization]);

  if (!useEmailCustomization) {
    return <></>;
  }

  return <div data-tb-test-id={SMTP_CREDENTIALS_SECTION}>
    {isEditMode(logic) ?
      <SMTPCredentialsEditMode editLogic={logic} credentialsErrorMessage={credentialsErrorMessage} /> :
      <SMTPCredentialsReadonlyMode
        readonlyLogic={logic}
        credentials={credentials}
      />}
  </div>;
}

const getSiteLuid = () => {
  return SessionSiteSelectors.siteLuid(getAppStore().getState());
};

interface SMTPCredentialsReadonlyModeProps {
  readonly readonlyLogic: InlineEditorLogic_ReadonlyMode;
  readonly credentials: EmailCustomizationFormFields;
}

export function SMTPCredentialsReadonlyMode(props: SMTPCredentialsReadonlyModeProps) {
  const { readonlyLogic, credentials } = props;

  const hasCredentials = !!credentials.host;
  const localName = credentials.localName || DEFAULT_SENDER_LOCAL_NAME;
  const credentialsSection = <div className={readonlyCredentialsClass} data-tb-test-id={READONLY_CREDENTIALS_SECTION}>
    <div data-tb-test-id={READONLY_SENT_FROM_TEST_ID}><span style={{ fontWeight: 'bold' }}>{Messages.emailServer_emailSentFrom()}</span><span>{(credentials.friendlyName || DEFAULT_SENDER_FRIENDLY_NAME) + ` <${localName}@${credentials.domain}>`}</span></div>
    <div data-tb-test-id={READONLY_DOMAIN_TEST_ID}><span style={{ fontWeight: 'bold' }}>{Messages.emailServer_domain_colon()}</span><span>{credentials.domain}</span></div>
    <div data-tb-test-id={READONLY_SERVER_ADDRESS_TEST_ID}><span>{Messages.emailServer_smtpServerAddress_colon()}</span><span>{credentials.host}</span></div>
    <div data-tb-test-id={READONLY_PORT_TEST_ID}><span>{Messages.emailServer_portNumber_colon()}</span><span>{credentials.port}</span></div>
  </div>;

  return (
    <>
      {hasCredentials && credentialsSection}
      <div style={{ paddingTop: 4 }}>
        <ButtonWidget
          style={readonlyLogic.canEdit ? undefined : { visibility: 'hidden' }}
          disabled={!readonlyLogic.canEdit}
          onClick={() => readonlyLogic.enterEditMode()}
          children={hasCredentials ? Messages.edit() : Messages.emailServer_addButton()}
          testId={'edit-server-credentials-button'}
        />
      </div>
    </>
  );
}

interface SMTPCredentialsEditModeProps {
  readonly editLogic: InlineEditorLogic_EditMode<CredentialsEditorState>;
  readonly credentialsErrorMessage?: string;
}

export function SMTPCredentialsEditMode(props: SMTPCredentialsEditModeProps) {
  const { editLogic, credentialsErrorMessage } = props;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.key === 'Enter' && editLogic.canSaveThisEditorState) {
      editLogic.startSave();
    }
  };

  const truncateText = (input: string, maxLength: number) => {
    return input.substring(0, maxLength);
  };

  return (
    <>
      <div style={{ width: INPUT_WIDE_WIDTH }}>
        <div style={{ paddingBottom: 4 }}>
          <div className={classes(emailCustomizationInputClass, wideField)}>
            <TextFieldWidget
              label={Messages.emailServer_smtpServerAddress()}
              onChange={e => editLogic.setEditorStateWithinEditMode({
                host:
                  truncateText(e.target.value, MAX_DOMAIN_INPUT_LENGTH)
              })}
              onClear={() => editLogic.setEditorStateWithinEditMode({ host: '' })}
              onKeyDown={handleKeyDown}
              required
              testId={EDIT_SERVER_ADDRESS_FIELD_TEST_ID}
              valid={!isHostValid(editLogic.editorState.host) ? false : undefined}
              value={editLogic.editorState.host}
            />
            <TextFieldWidget
              label={Messages.emailServer_portNumber()}
              onChange={e => editLogic.setEditorStateWithinEditMode({
                port:
                  truncateText(e.target.value, MAX_GENERAL_INPUT_LENGTH)
              })}
              onClear={() => editLogic.setEditorStateWithinEditMode({ port: '' })}
              onKeyDown={handleKeyDown}
              placeholder={Messages.emailServer_portPlaceholder()}
              required
              testId={EDIT_PORT_FIELD_TEST_ID}
              valid={!isPortValid(editLogic.editorState.port) ? false : undefined}
              value={editLogic.editorState.port}
            />
            <TextFieldWidget
              label={Messages.emailServer_username()}
              onChange={e => editLogic.setEditorStateWithinEditMode({
                username:
                  truncateText(e.target.value, MAX_GENERAL_INPUT_LENGTH)
              })}
              onClear={() => editLogic.setEditorStateWithinEditMode({ username: '' })}
              onKeyDown={handleKeyDown}
              required
              testId={EDIT_USERNAME_FIELD_TEST_ID}
              valid={!isUsernameValid(editLogic.editorState.username) ? false : undefined}
              value={editLogic.editorState.username}
            />
            <TextFieldWidget
              label={Messages.password()}
              onChange={e => editLogic.setEditorStateWithinEditMode({
                password:
                  truncateText(e.target.value, MAX_GENERAL_INPUT_LENGTH)
              })}
              onClear={() => editLogic.setEditorStateWithinEditMode({ password: '' })}
              onKeyDown={handleKeyDown}
              required
              testId={EDIT_PASSWORD_FIELD_TEST_ID}
              type='password'
              valid={!isPasswordValid(editLogic.editorState.password) ? false : undefined}
              value={editLogic.editorState.password}
            />
            <TextFieldWidget
              label={Messages.emailServer_domain()}
              onChange={e => editLogic.setEditorStateWithinEditMode({
                domain:
                  truncateText(e.target.value, MAX_DOMAIN_INPUT_LENGTH)
              })}
              onClear={() => editLogic.setEditorStateWithinEditMode({ domain: '' })}
              onKeyDown={handleKeyDown}
              required
              testId={EDIT_DOMAIN_FIELD_TEST_ID}
              valid={!isHostValid(editLogic.editorState.domain) ? false : undefined}
              value={editLogic.editorState.domain}
            />
            <TextFieldWidget
              label={Messages.emailServer_friendlyNameFieldLabel()}
              onChange={e => editLogic.setEditorStateWithinEditMode({
                friendlyName:
                  truncateText(e.target.value, MAX_GENERAL_INPUT_LENGTH)
              })}
              onClear={() => editLogic.setEditorStateWithinEditMode({ friendlyName: '' })}
              onKeyDown={handleKeyDown}
              testId={EDIT_FRIENDLY_NAME_FIELD_TEST_ID}
              valid={!isFriendlyNameValid(editLogic.editorState.friendlyName) ? false : undefined}
              value={editLogic.editorState.friendlyName}
            />
            <TextFieldWidget
              label={Messages.emailServer_localName()}
              onChange={e => editLogic.setEditorStateWithinEditMode({
                localName:
                  truncateText(e.target.value, MAX_GENERAL_INPUT_LENGTH)
              })}
              onClear={() => editLogic.setEditorStateWithinEditMode({ localName: '' })}
              onKeyDown={handleKeyDown}
              testId={EDIT_LOCALNAME_FIELD_TEST_ID}
              valid={!isLocalNameValid(editLogic.editorState.localName) ? false : undefined}
              value={editLogic.editorState.localName}
            />
          </div>
        </div>
        {credentialsErrorMessage && <CompactInlineAlertWidget
          children={<span>{credentialsErrorMessage}</span>}
          style={{ marginTop: 4, marginBottom: 4 }}
          type={InlineAlertType.Error}
          testId={CREDENTIALS_FORM_ERROR_TEST_ID}
        />}
        <div style={{ paddingTop: 4 }} className={credentialsFormButtonsClass}>
          <InlineEditorCancelButton {...editLogic} />
          <span style={{ paddingLeft: 8 }}>
            <InlineEditorSaveButton {...editLogic} />
          </span>
        </div>
      </div>
    </>
  );
}

function isStringValid(input: string, maxLength: number): boolean {
  return !_.isUndefined(input) && input.trim().length > 0 && input.length <= maxLength;
}

function isHostValid(host: string): boolean {
  return !host || (isStringValid(host, MAX_DOMAIN_INPUT_LENGTH) && isValidDomain(host));
}

function isPortValid(port: string): boolean {
  // undefined/empty/blank are treated as valid
  if (!port) { return true; }

  // non-digit strings are invalid
  if (!(/^\d*$/.exec(port))) { return false; }

  const portNumber = parseInt(port, 10);

  return portNumber > 0 && portNumber <= MAX_PORT;
}

function isUsernameValid(username: string): boolean {
  return !username || isStringValid(username, MAX_GENERAL_INPUT_LENGTH);
}

function isLocalNameValid(localName: string): boolean {
  /* The following regex checks whether the provided local name is a valid local name. Criteria is:
    alphanumeric, with some special characters, period as long as it's not first
    nor last nor consecutive with another period, and quotes strings allow additional
    characters but must start and end with double quotes (RFC 5321, RFC 5322).
    Examples of valid local names: simple, simple.extra, hello-simple+name,
    "quoted.@,spaces", user@[IPv6:2001:db8::1] */
  const localNameRegex = new RegExp('^[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~-]+' +
    '(?:\\.[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~-]+)*$|' +
    '^\"(?:[\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x21\\x23-\\x5b\\x5d-\\x7f]|' +
    '\\\\[\\x01-\\x09\\x0b\\x0c\\x0e-\\x7f])+\"$');
  return !localName || (isStringValid(localName, MAX_GENERAL_INPUT_LENGTH) && localNameRegex.test(localName));
}

function isPasswordValid(password: string): boolean {
  return !password || isStringValid(password, MAX_GENERAL_INPUT_LENGTH);
}

function isFriendlyNameValid(friendlyName: string): boolean {
  return !friendlyName || isStringValid(friendlyName, MAX_GENERAL_INPUT_LENGTH);
}